import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Guide, HistoryData, PartsOfTheDay, TimeToTakePills } from "../types";

dayjs.extend(customParseFormat);

export function isNumeric(value: unknown): boolean {
  return !isNaN(Number(value)) && !isNaN(parseFloat(String(value)));
}

export const PARTS_OF_THE_DAY: PartsOfTheDay = {
  morning: {
    labelHe: "בוקר",
    partOfTheDay: "morning",
    value: "",
  },
  noon: {
    labelHe: "צהריים",
    partOfTheDay: "noon",
    value: "",
  },
  evening: {
    labelHe: "ערב",
    partOfTheDay: "evening",
    value: "",
  },
  night: {
    labelHe: "לילה",
    partOfTheDay: "night",
    value: "",
  },
  sos: {
    labelHe: "חרום",
    partOfTheDay: "sos",
    value: "",
  },
};

export const getPartOfTheDayByTime = (): TimeToTakePills => {
  const currentHour = dayjs().hour();

  if (currentHour >= 6 && currentHour < 12) {
    return "morning";
  }
  if (currentHour >= 12 && currentHour < 18) {
    return "noon";
  }
  if (currentHour >= 18 && currentHour < 22.5) {
    return "evening";
  }

  return "night";
};

export const isValidIsraeliId = (id: string) => {
  // הסרת רווחים מיותרים
  id = id.trim();

  // בדיקת אורך התעודת זהות (חייב להיות 9 ספרות)
  if (!/^\d{1,9}$/.test(id)) return false;

  // הוספת אפסים לתחילת המספר במידת הצורך, כך שיהיו 9 ספרות
  id = id.padStart(9, "0");

  let sum = 0;

  for (let i = 0; i < 9; i++) {
    let num = parseInt(id[i], 10);

    // הכפלת המספר במיקום אי-זוגי ב-1 ובזוגי ב-2
    let multiplied = num * ((i % 2) + 1);

    // אם התוצאה גדולה מ-9, נחבר את ספרותיה
    if (multiplied > 9) multiplied -= 9;

    sum += multiplied;
  }

  // תעודת זהות תקינה אם סכום הספרות מתחלק ב-10 ללא שארית
  return sum % 10 === 0;
};

export function createExcelFileByMonth(
  history: HistoryData,
  guides: Guide[],
  patientName: string
) {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  const allData: any[] = [];

  Object.keys(history).forEach((date) => {
    const dayData = history[date];

    // Group pills by month
    Object.keys(dayData).forEach((partOfTheDay) => {
      const guideData = dayData[partOfTheDay];

      Object.entries(guideData).forEach(([guideId, pills]) => {
        pills.forEach((pill) => {
          // Create a row of pill data
          const row = {
            תאריך: date,
            // @ts-ignore
            "חלק ביום": PARTS_OF_THE_DAY[partOfTheDay].labelHe,
            מדריך: guides.find((g) => g._id === guideId)?.name || "",
            "שם הכדור": pill.pillName,
            מינון: pill.dosage,
            שעה: pill.time,
            SOS: pill.isSos ? "כן" : "לא",
          };

          allData.push(row);
        });
      });
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(allData);
  XLSX.utils.book_append_sheet(workbook, worksheet, "הסטוריית כדורים");

  // Generate a binary Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  // Save the file using FileSaver.js
  saveAs(
    new Blob([excelBuffer], { type: "application/octet-stream" }),
    `${patientName}_${dayjs().year()}_היסטוריית_כדורים.xlsx`
  );
}

export const isGreaterThanZero = (value: string) => {
  const regex = /^(0|[1-9]\d*)(\.\d*)?$|^0?\.$/;
  return regex.test(value) && parseFloat(value) >= 0;
};

export const reverseHebrew = (text: string): string => {
  if (!text) return "";
  const isHebrew = /[\u0590-\u05FF]/.test(text);
  return isHebrew ? text.split("").reverse().join("") : text;
};
